import { useEffect, useContext } from "react";
import {
    CurrentAttachmentContext,
    DataContext,
    RegionOfInterestContext
} from "~common/form/icg-definition-data-provider";


const OCRDataSupervisor = () => {
    const [currentAttachment, setCurrentAttachment, currentPage, setCurrentPage, rotation, setRotation, scaleMode, setScaleMode, roiPath, setRoiPath, icgOCRData, setICGOCRData, rawOCRData, setRawOCRData, suppressOCRData, setSuppressOCRData] = useContext(CurrentAttachmentContext);
    const { selectedRegion, setSelectedRegion, regionsOfInterest, setRegionsOfInterest, mouseSelectionMode, setMouseSelectionMode, mouseModeUserSelectable, setMouseModeUserSelectable, minRegionWidth, setMinRegionWidth, minRegionHeight, setMinRegionHeight } = useContext(RegionOfInterestContext);

    useEffect(() => {
        const fetchOCRData = async () => {
            if (!currentAttachment) {
                console.warn("No current attachment to fetch OCR data for");
                return;
            }
            try {
                const response = await fetch('/api/data-extraction/ocr/' + currentAttachment.storageKey + '/' + currentPage + '/data'); // Adjust the endpoint as necessary
                if (response.ok) {
                    const data = await response.json();
                    setRawOCRData(data);
                } else {
                    console.error("Failed to fetch OCR data", response.status, response.statusText);
                }
            } catch (error) {
                console.error("Error fetching OCR data:", error);
            }
        };
        
        if (!suppressOCRData && icgOCRData) {
            fetchOCRData();
        }


    }, [currentAttachment, currentPage]);

    useEffect(() => {
        if (icgOCRData && icgOCRData.paths) {

            if (roiPath) {
                //trim the leading "/" from the roiPath, if it exists
                var path = roiPath.startsWith("/") ? roiPath.substring(1) : roiPath;
                //find the path that matches the roiPath

                const matchingPath = icgOCRData.paths[path];
                if (matchingPath) {
                    console.log("Found matching path:", matchingPath);
                    var pageIndex = matchingPath?.PageIndex;
                    var bb = matchingPath?.Geometry?.BoundingBox;
                    if (bb) {
                        const pathRegion = {
                            x: bb.Left,
                            y: bb.Top,
                            width: bb.Width,
                            height: bb.Height,
                            style: {
                                stroke: 'rgba(247, 184, 108, 0.6)',
                                fill: 'rgba(247, 184, 108, 0.3)',
                                strokeWidth: 1
                            }
                        };
                        setRegionsOfInterest([pathRegion]);
                        if (pageIndex != null) {
                            setCurrentPage(pageIndex + 1);
                        }
                    }
                } else {
                    setRoiPath(null);
                    setRegionsOfInterest([]);

                }

            }

        }


    }, [roiPath, icgOCRData]);

    useEffect(() => {
        if (rawOCRData) {
            setMouseModeUserSelectable(true)
        } else {
            setMouseModeUserSelectable(false);
        }
    }, [rawOCRData]);

    useEffect(() => {
        if (regionsOfInterest && regionsOfInterest.length > 0) {
            const recent = regionsOfInterest[regionsOfInterest.length - 1];
            if (recent.userSelection && recent.selectionStart && (recent.words === undefined || recent.words === null)) {
                var matchLevel = 1;
                if (recent.selectionStart.indexOf("Right") > -1) {
                    matchLevel = 0;
                }
    
                recent.words = findWordsInRegion(currentPage - 1, recent, matchLevel, rawOCRData);
    
                if (recent.words && recent.words.length > 0) {
                    recent.style = {
                        stroke: 'rgba(76, 170, 109, 0.6)',
                        fill: 'rgba(76, 170, 109, 0.3)',
                        strokeWidth: 1
                    };
    
                    // Insert \r\n if Top of current word is greater than Top of previous word
                    let lineBottom = null;
                    let lastRight = null;
                    const contents = recent.words.map((word, index) => {
                        const text = word.text;
                        if (lineBottom === null) {
                            lineBottom = word.boundingBox.Top + word.boundingBox.Height;
                            return text;
                        }
                        if (lastRight === null) {
                            lastRight = word.boundingBox.Left + word.boundingBox.Width;
                        }
                        let out = "";
                        if (word.boundingBox.Top > lineBottom ) {
                            out += "\r\n";
                            lastRight = null
                        } else {

                            if (lastRight && word.boundingBox.Left > (lastRight + word.boundingBox.Height)) {
                                out += "\t";
                            } else {
                                out += " ";
                            }
                        }

                        lineBottom = word.boundingBox.Top + word.boundingBox.Height;
                        lastRight = word.boundingBox.Left + word.boundingBox.Width;

                        return out + text;
                    }).join("");
    
                    copyToClipboard(contents);
                } else {
                    recent.style = {
                        stroke: 'rgba(241, 239, 84, 0.6)',
                        fill: 'rgba(241, 239, 84, 0.3)',
                        strokeWidth: 1
                    };
                }
    
                setRegionsOfInterest([recent]);
            }
        }
    }, [regionsOfInterest]);
    

    return null; // No UI
};

async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
    } catch (err) {
        console.error("Failed to copy text:", err);
    }
}

const checkBoundingBoxRelation = (selectedRegion, block) => {
    const sLeft = selectedRegion.x;
    const sRight = selectedRegion.x + selectedRegion.width;
    const sTop = selectedRegion.y;
    const sBottom = selectedRegion.y + selectedRegion.height;
  
    const tLeft = block.Left;
    const tRight = block.Left + block.Width;
    const tTop = block.Top;
    const tBottom = block.Top + block.Height;
  
    // Check if target is completely inside selected
    if (
      tLeft >= sLeft &&
      tRight <= sRight &&
      tTop >= sTop &&
      tBottom <= sBottom
    ) {
      return 1;
    }
  
    // Check if target intersects selected
    const isIntersecting =
      tLeft < sRight &&
      tRight > sLeft &&
      tTop < sBottom &&
      tBottom > sTop;
  
    if (isIntersecting) {
      return 0;
    }
  
    // Target is completely outside
    return -1;
  }

const findWordsInRegion = (pageIndex, region, matchLevel, icgOCRData) => {
    
    // This function should find words in the given region based on the icgOCRData
    // For now, we will just return a dummy phrase
    var words:Array<any> = []
    if (icgOCRData) {
        if(icgOCRData["ExpenseDocuments"] && icgOCRData["ExpenseDocuments"].length > 0){
            var doc;
            if (icgOCRData["ExpenseDocuments"][pageIndex]) {
                doc = icgOCRData["ExpenseDocuments"][pageIndex];
            } else {
                doc = icgOCRData["ExpenseDocuments"][0]; // Fallback to the first document if pageIndex is out of bounds
            }

            if (doc.Blocks && doc.Blocks.length > 0) {
                doc.Blocks.forEach((block) => {
                    if(block.BlockType == "WORD"){
                        const boundingBox = block.Geometry.BoundingBox;
                        if (checkBoundingBoxRelation(region, boundingBox) >= matchLevel) {
                            words.push({
                                text: block.Text,
                                boundingBox:  block.Geometry.BoundingBox
                            });
                        }
                    }
                   
                });
            }
        }

    }

    return words 
}

export default OCRDataSupervisor;
